import Caretaker from '@/models/Caretaker'
import SorewardsIssueTransaction from '@/models/SorewardsIssueTransaction'
import SorewardsLoadTransaction from '@/models/SorewardsLoadTransaction'
import SorewardsNegotiatedRate from '@/models/SorewardsNegotiatedRate'
import SorewardsStandardRate from '@/models/SorewardsStandardRate'
import { cloneGrid, mockState } from '@/services/sorewards/mock/fixtures'
import {
  IssueFilters,
  IssueListResult,
  IssueRow,
  IssueStats,
  IssueTransaction,
  LoadFilters,
  LoadListResult,
  LoadRow,
  LoadStats,
  LoadTransaction,
  NegotiatedRates,
  ProgramMapping,
  RateGrid,
  RATE_GRID_KEYS,
  ResolvedCost,
  StandardRates
} from '@/services/sorewards/types'

function useConsoleApi() {
  return process.env.VUE_APP_SOREWARDS_CONSOLE_API === 'true'
}

function round2(value: number) {
  return +value.toFixed(2)
}

function pickGrid(source: RateGrid): RateGrid {
  return {
    issuePhysical: source.issuePhysical,
    issueVirtual: source.issueVirtual,
    shipUnit: source.shipUnit,
    shipGrouped: source.shipGrouped,
    loadFlat: source.loadFlat,
    loadPct: source.loadPct
  }
}

function caretakerMatches(
  row: { caretakerId: string, caretakerName: string },
  caretakerId: string,
  caretakerName: string
) {
  if (caretakerId && row.caretakerId) return row.caretakerId === caretakerId
  if (caretakerName) {
    return row.caretakerName.toLowerCase().includes(caretakerName.trim().toLowerCase())
  }
  if (caretakerId && !row.caretakerId) return false
  return true
}

function inRange(iso: string, from?: string, to?: string) {
  return (!from || iso >= from) && (!to || iso <= to)
}

function includesText(value: string, query?: string) {
  if (!query || !query.trim()) return true
  return String(value).toLowerCase().includes(query.trim().toLowerCase())
}

function resolveGrid(
  caretakerId: string,
  caretakerName: string,
  standard: RateGrid,
  negotiated: NegotiatedRates[]
) {
  const match = negotiated.find(row => {
    if (caretakerId && row.caretakerId) return row.caretakerId === caretakerId
    return !!caretakerName && row.caretakerName === caretakerName
  })

  return {
    grid: match || standard,
    negotiated: !!match
  }
}

export function resolveLoadCost(
  row: LoadTransaction,
  standard: RateGrid,
  negotiated: NegotiatedRates[]
): ResolvedCost {
  const resolved = resolveGrid(row.caretakerId, row.caretakerName, standard, negotiated)
  const value = row.loadCost != null
    ? row.loadCost
    : round2(resolved.grid.loadFlat + row.amount * resolved.grid.loadPct / 100)
  const isNegotiated = row.negotiated != null ? row.negotiated : resolved.negotiated

  return {
    value,
    negotiated: isNegotiated,
    caretakerShare: round2(value * row.coverage / 100)
  }
}

export function resolveIssueCost(
  row: IssueTransaction,
  standard: RateGrid,
  negotiated: NegotiatedRates[]
): ResolvedCost {
  const resolved = resolveGrid(row.caretakerId, row.caretakerName, standard, negotiated)
  const computed = row.cardType === 'physical'
    ? resolved.grid.issuePhysical
    : resolved.grid.issueVirtual
  const value = row.issuePrice != null ? row.issuePrice : round2(computed)
  const isNegotiated = row.negotiated != null ? row.negotiated : resolved.negotiated

  return {
    value,
    negotiated: isNegotiated,
    caretakerShare: round2(value * row.coverage / 100)
  }
}

function paginate<T>(rows: T[], page: number, per: number) {
  const start = (page - 1) * per
  return rows.slice(start, start + per)
}

function standardFromModel(model: SorewardsStandardRate): StandardRates {
  return {
    id: model.id,
    ...pickGrid(model)
  }
}

function negotiatedFromModel(model: SorewardsNegotiatedRate): NegotiatedRates {
  return {
    id: model.id,
    caretakerId: model.caretaker?.id || '',
    caretakerName: model.caretaker?.name || '',
    validUntil: model.validUntil,
    ...pickGrid(model)
  }
}

function loadFromModel(model: SorewardsLoadTransaction): LoadTransaction {
  return {
    id: model.id,
    idDistribution: model.idDistribution,
    caretakerId: model.caretaker?.id || '',
    caretakerName: model.caretaker?.name || model.caretakerName || '',
    programFamily: model.programFamily,
    dotationOrder: model.dotationOrder,
    dotationType: model.dotationType as LoadTransaction['dotationType'],
    dotationDate: model.dotationDate,
    loadDate: model.loadDate,
    loadRef: model.loadRef,
    expiry: model.expiry,
    amount: model.amount,
    beneficiary: model.beneficiary,
    cardId: model.cardId,
    remaining: model.remaining,
    coverage: model.coverage,
    loadCost: model.loadCost,
    negotiated: model.negotiated
  }
}

function issueFromModel(model: SorewardsIssueTransaction): IssueTransaction {
  return {
    id: model.id,
    cardId: model.cardId,
    caretakerId: model.caretaker?.id || '',
    caretakerName: model.caretaker?.name || model.caretakerName || '',
    dotationOrder: model.dotationOrder,
    beneficiary: model.beneficiary,
    issueDate: model.issueDate,
    cardNumber: model.cardNumber,
    expiry: model.expiry,
    cardType: model.cardType as IssueTransaction['cardType'],
    coverage: model.coverage,
    issuePrice: model.issuePrice,
    negotiated: model.negotiated
  }
}

function filterLoads(filters: LoadFilters) {
  return mockState.loads.filter(row =>
    caretakerMatches(row, filters.caretakerId || '', filters.caretakerName || '') &&
    inRange(row.loadDate, filters.dateFrom, filters.dateTo) &&
    includesText(row.idDistribution, filters.idDistribution) &&
    includesText(row.dotationOrder, filters.dotationOrder) &&
    includesText(row.beneficiary, filters.beneficiary) &&
    includesText(row.cardId, filters.cardId)
  )
}

function filterIssues(filters: IssueFilters) {
  return mockState.issues.filter(row =>
    caretakerMatches(row, filters.caretakerId || '', filters.caretakerName || '') &&
    inRange(row.issueDate, filters.dateFrom, filters.dateTo) &&
    includesText(row.cardId, filters.cardId) &&
    includesText(row.dotationOrder, filters.dotationOrder) &&
    includesText(row.beneficiary, filters.beneficiary)
  )
}

function loadStatsFor(rows: LoadRow[], totalCount: number): LoadStats {
  return {
    totalLoaded: rows.reduce((sum, row) => sum + row.amount, 0),
    totalRemaining: rows.reduce((sum, row) => sum + row.remaining, 0),
    totalLoadCost: round2(rows.reduce((sum, row) => sum + row.cost.value, 0)),
    cardsCount: new Set(rows.map(row => row.cardId)).size,
    filteredCount: rows.length,
    totalCount
  }
}

function issueStatsFor(rows: IssueRow[], totalCount: number): IssueStats {
  return {
    cardsIssued: rows.length,
    physicalCount: rows.filter(row => row.cardType === 'physical').length,
    virtualCount: rows.filter(row => row.cardType === 'virtual').length,
    totalIssueCost: round2(rows.reduce((sum, row) => sum + row.cost.value, 0)),
    filteredCount: rows.length,
    totalCount
  }
}

function withLoadCosts(
  rows: LoadTransaction[],
  standard: RateGrid,
  negotiated: NegotiatedRates[]
): LoadRow[] {
  return rows.map(row => ({
    ...row,
    cost: resolveLoadCost(row, standard, negotiated)
  }))
}

function withIssueCosts(
  rows: IssueTransaction[],
  standard: RateGrid,
  negotiated: NegotiatedRates[]
): IssueRow[] {
  return rows.map(row => ({
    ...row,
    cost: resolveIssueCost(row, standard, negotiated)
  }))
}

type FilterValue = string | { prefix?: string, gte?: string, lte?: string }
type GraphitiStatNode = number | { count?: number, sum?: number }

function readStat(
  meta: { stats?: Record<string, GraphitiStatNode> } | undefined,
  keys: string[],
  fallback: number
) {
  const stats = meta?.stats
  if (!stats) return fallback
  for (const key of keys) {
    const node = stats[key]
    if (node == null) continue
    if (typeof node === 'number') return node
    if (typeof node.count === 'number') return node.count
    if (typeof node.sum === 'number') return node.sum
  }
  return fallback
}

function assignGrid(target: RateGrid, source: RateGrid) {
  RATE_GRID_KEYS.forEach(key => {
    target[key] = source[key]
  })
}

export async function getStandardRates(): Promise<StandardRates> {
  if (!useConsoleApi()) {
    return { ...mockState.standard }
  }

  const { data } = await SorewardsStandardRate.page(1).per(1).all()
  if (!data.length) {
    return { id: '', ...cloneGrid(mockState.standard) }
  }
  return standardFromModel(data[0])
}

export async function saveStandardRates(rates: StandardRates): Promise<StandardRates> {
  if (!useConsoleApi()) {
    mockState.standard = { ...rates }
    return { ...mockState.standard }
  }

  let record: SorewardsStandardRate
  if (rates.id) {
    const { data } = await SorewardsStandardRate.find(rates.id)
    record = data
  } else {
    record = new SorewardsStandardRate()
  }
  assignGrid(record, rates)
  await record.save()
  return standardFromModel(record)
}

export async function listNegotiatedRates(): Promise<NegotiatedRates[]> {
  if (!useConsoleApi()) {
    return mockState.negotiated.map(row => ({ ...row }))
  }

  const { data } = await SorewardsNegotiatedRate.includes(['caretaker']).all()
  return data.map(negotiatedFromModel)
}

export async function saveNegotiatedRate(row: NegotiatedRates): Promise<NegotiatedRates> {
  if (!useConsoleApi()) {
    const payload = { ...row }
    const index = mockState.negotiated.findIndex(item => item.id === row.id)
    if (index >= 0) {
      mockState.negotiated.splice(index, 1, payload)
    } else {
      payload.id = payload.id || `n${Date.now()}`
      mockState.negotiated.push(payload)
    }
    return { ...payload }
  }

  let record: SorewardsNegotiatedRate
  if (row.id) {
    const { data } = await SorewardsNegotiatedRate.includes(['caretaker']).find(row.id)
    record = data
  } else {
    record = new SorewardsNegotiatedRate()
  }

  assignGrid(record, row)
  record.validUntil = row.validUntil
  if (row.caretakerId) {
    record.caretaker = new Caretaker({ id: row.caretakerId })
  }
  await record.save()

  if (record.id) {
    const { data } = await SorewardsNegotiatedRate.includes(['caretaker']).find(record.id)
    return negotiatedFromModel(data)
  }
  return negotiatedFromModel(record)
}

export async function deleteNegotiatedRate(id: string): Promise<void> {
  if (!useConsoleApi()) {
    mockState.negotiated = mockState.negotiated.filter(row => row.id !== id)
    return
  }

  const { data } = await SorewardsNegotiatedRate.find(id)
  await data.destroy()
}

export async function listProgramMappings(): Promise<ProgramMapping[]> {
  return mockState.mappings.map(row => ({ ...row }))
}

export async function listLoadTransactions(filters: LoadFilters): Promise<LoadListResult> {
  const [standard, negotiated] = await Promise.all([
    getStandardRates(),
    listNegotiatedRates()
  ])

  if (!useConsoleApi()) {
    const filtered = withLoadCosts(filterLoads(filters), standard, negotiated)
    return {
      data: paginate(filtered, filters.page, filters.per),
      stats: loadStatsFor(filtered, mockState.loads.length)
    }
  }

  let scope = SorewardsLoadTransaction
    .includes(['caretaker'])
    .page(filters.page)
    .per(filters.per)
    .order({ loadDate: 'desc' })
    .stats({ total: 'count', amount: 'sum', remaining: 'sum', loadCost: 'sum' })

  const where: Record<string, FilterValue> = {}
  if (filters.caretakerId) where.caretakerId = filters.caretakerId
  if (filters.dateFrom || filters.dateTo) {
    where.loadDate = {
      ...(filters.dateFrom ? { gte: filters.dateFrom } : {}),
      ...(filters.dateTo ? { lte: filters.dateTo } : {})
    }
  }
  if (filters.idDistribution) where.idDistribution = { prefix: filters.idDistribution }
  if (filters.dotationOrder) where.dotationOrder = { prefix: filters.dotationOrder }
  if (filters.beneficiary) where.beneficiary = { prefix: filters.beneficiary }
  if (filters.cardId) where.cardId = { prefix: filters.cardId }
  if (Object.keys(where).length) scope = scope.where(where)

  const { data, meta } = await scope.all()
  const rows = withLoadCosts(data.map(loadFromModel), standard, negotiated)
  const fallback = loadStatsFor(rows, rows.length)

  return {
    data: rows,
    stats: {
      totalLoaded: readStat(meta, ['amount', 'totalLoaded'], fallback.totalLoaded),
      totalRemaining: readStat(meta, ['remaining', 'totalRemaining'], fallback.totalRemaining),
      totalLoadCost: readStat(meta, ['loadCost', 'totalLoadCost'], fallback.totalLoadCost),
      cardsCount: readStat(meta, ['cardsCount', 'cardId'], fallback.cardsCount),
      filteredCount: readStat(meta, ['total', 'filteredCount'], fallback.filteredCount),
      totalCount: readStat(meta, ['total'], fallback.totalCount)
    }
  }
}

export async function listIssueTransactions(filters: IssueFilters): Promise<IssueListResult> {
  const [standard, negotiated] = await Promise.all([
    getStandardRates(),
    listNegotiatedRates()
  ])

  if (!useConsoleApi()) {
    const filtered = withIssueCosts(filterIssues(filters), standard, negotiated)
    return {
      data: paginate(filtered, filters.page, filters.per),
      stats: issueStatsFor(filtered, mockState.issues.length)
    }
  }

  let scope = SorewardsIssueTransaction
    .includes(['caretaker'])
    .page(filters.page)
    .per(filters.per)
    .order({ issueDate: 'desc' })
    .stats({ total: 'count', issuePrice: 'sum' })

  const where: Record<string, FilterValue> = {}
  if (filters.caretakerId) where.caretakerId = filters.caretakerId
  if (filters.dateFrom || filters.dateTo) {
    where.issueDate = {
      ...(filters.dateFrom ? { gte: filters.dateFrom } : {}),
      ...(filters.dateTo ? { lte: filters.dateTo } : {})
    }
  }
  if (filters.cardId) where.cardId = { prefix: filters.cardId }
  if (filters.dotationOrder) where.dotationOrder = { prefix: filters.dotationOrder }
  if (filters.beneficiary) where.beneficiary = { prefix: filters.beneficiary }
  if (Object.keys(where).length) scope = scope.where(where)

  const { data, meta } = await scope.all()
  const rows = withIssueCosts(data.map(issueFromModel), standard, negotiated)
  const fallback = issueStatsFor(rows, rows.length)

  return {
    data: rows,
    stats: {
      cardsIssued: readStat(meta, ['total', 'cardsIssued'], fallback.cardsIssued),
      physicalCount: readStat(meta, ['physicalCount'], fallback.physicalCount),
      virtualCount: readStat(meta, ['virtualCount'], fallback.virtualCount),
      totalIssueCost: readStat(meta, ['issuePrice', 'totalIssueCost'], fallback.totalIssueCost),
      filteredCount: readStat(meta, ['total', 'filteredCount'], fallback.filteredCount),
      totalCount: readStat(meta, ['total'], fallback.totalCount)
    }
  }
}

export async function listAllLoadTransactions(filters: Omit<LoadFilters, 'page' | 'per'>): Promise<LoadRow[]> {
  const result = await listLoadTransactions({ ...filters, page: 1, per: 10000 })
  return result.data
}

export async function listAllIssueTransactions(filters: Omit<IssueFilters, 'page' | 'per'>): Promise<IssueRow[]> {
  const result = await listIssueTransactions({ ...filters, page: 1, per: 10000 })
  return result.data
}
